import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { type PermissionApp, adminClient } from '@/api/adminClient';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { TablePager } from '@/components/table/TablePager';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/format';
import { orgBasePath } from '@/lib/orgNav';

import { OrgSwitcher } from '../Users/UsersPage';
import { type OpenRequest, DecideButtons } from './RequestsTab';
import { bareApp, useOrgChoice } from './names';
import { type AppCaller, type AppCallers, type AppRequest, errorText, permissionsApi } from './permissionsApi';
import { BackLink, Problem, givenBy, untilText } from './shared';

const PAGE_SIZE = 25;
const COLUMNS = [{ label: 'App' }, { label: 'Owners' }, { label: 'Members', className: 'hidden md:table-cell' }, { label: 'May call', className: 'hidden md:table-cell' }];

/** The integrations an app may call: its own grants and the ones given to every app (`app:*`). */
export const callsOf = (callers: AppCaller[], appId: string) => callers.filter((c) => c.app === appId || c.app === '*');
const integrationNames = (callers: AppCaller[], appId: string) => [...new Set(callsOf(callers, appId).map((c) => c.integration))].sort();

/** What the grant lists could not show: an integration that failed, and one cut at the read limit. Empty when the list is whole. */
export function callerNotes(callers: AppCallers): string[] {
  return [
    ...Object.entries(callers.cut).map(([integration, n]) => `${integration}: list cut at ${n}, so an app may call it and not show here.`),
    ...Object.entries(callers.failed).map(([integration, error]) => `${integration}: could not be read. ${error}`),
  ];
}

const ownersOf = (app: PermissionApp) => app.members.filter((m) => m.role === 'owner').map((m) => m.label).join(', ') || '–';

export function AppsTab({ open, onOpen }: { open?: string; onOpen: (id?: string) => void }) {
  const { orgs, orgId, choose, error: orgError } = useOrgChoice();
  const [page, setPage] = useState<{ items: PermissionApp[]; total: number }>();
  const [callers, setCallers] = useState<AppCallers>();
  const [error, setError] = useState<string>();
  const [callerError, setCallerError] = useState<string>();
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const search = useDebouncedValue(query.trim(), 300);

  // The detail finds its app by project id, which the server search matches, so a link works from any page of the list.
  const load = useCallback(async () => {
    if (!orgId) return;
    setError(undefined);
    try {
      setPage(open ? await adminClient.listPermissionApps(orgId, { q: open, limit: 5 }) : await adminClient.listPermissionApps(orgId, { q: search.length >= 2 ? search : undefined, limit: PAGE_SIZE, offset }));
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [orgId, search, offset, open]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => setOffset(0), [search]);
  useEffect(() => {
    permissionsApi
      .appCallerGrants()
      .then(setCallers)
      .catch((failure) => setCallerError(errorText(failure)));
  }, []);

  const selected = open ? page?.items.find((app) => app.projectId === open) : undefined;
  if (open) return <AppDetail app={selected} loading={!page && !error} error={error} callers={callers} callerError={callerError} orgId={orgId} onBack={() => onOpen()} />;

  return (
    <div className="space-y-4">
      {callerError ? <Problem message={callerError} /> : null}
      {callers && callerNotes(callers).length > 0 ? <Problem message={callerNotes(callers).join(' ')} /> : null}
      <DataTable
        toolbar={
          <>
            <OrgSwitcher
              orgs={orgs}
              orgId={orgId}
              onChange={(next) => {
                choose(next);
                setPage(undefined);
                setOffset(0);
              }}
            />
            <SearchInput value={query} onChange={setQuery} placeholder="Search apps" label="Search apps" />
          </>
        }
        footer={page ? <TablePager total={page.total} limit={PAGE_SIZE} offset={offset} onOffsetChange={setOffset} /> : undefined}
      >
        <DataTableHead columns={COLUMNS} />
        {error || orgError ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error ?? orgError ?? ''} onRetry={() => void load()} />
        ) : !page ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading apps
          </DataTableMessage>
        ) : page.items.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>No app matches.</DataTableMessage>
        ) : (
          <tbody>
            {page.items.map((app) => (
              <DataTableRow key={app.projectId} to={`?tab=apps&open=${encodeURIComponent(app.projectId)}`}>
                <DataTableCell>
                  <button type="button" className="font-medium text-foreground hover:underline" onClick={() => onOpen(app.projectId)}>
                    {app.title}
                  </button>
                </DataTableCell>
                <DataTableCell className="text-muted-foreground">{ownersOf(app)}</DataTableCell>
                <DataTableCell className="hidden text-muted-foreground md:table-cell">{app.members.length}</DataTableCell>
                <DataTableCell className="hidden text-muted-foreground md:table-cell">{callers ? integrationNames(callers.rows, app.appId).join(', ') || (callerNotes(callers).length ? 'Unknown' : 'None') : '…'}</DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}

function AppDetail({ app, loading, error, callers, callerError, orgId, onBack }: { app?: PermissionApp; loading: boolean; error?: string; callers?: AppCallers; callerError?: string; orgId?: string; onBack: () => void }) {
  const [requests, setRequests] = useState<AppRequest[]>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const appId = app?.appId;

  const loadRequests = useCallback(async () => {
    if (!appId) return;
    try {
      setRequests((await permissionsApi.decidableApps()).filter((r) => bareApp(r.app) === appId));
    } catch (failure) {
      setProblem(errorText(failure));
    }
  }, [appId]);
  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const decide = async (row: OpenRequest, verdict: 'approve' | 'deny', reason?: string) => {
    setBusy(row.id);
    setProblem(undefined);
    try {
      await permissionsApi.decide('app', row.id, verdict, reason);
    } catch (failure) {
      setProblem(errorText(failure)); // the service's text, for example "already closed"
    } finally {
      setBusy(undefined);
      await loadRequests();
    }
  };

  const rows = useMemo(() => (app ? callsOf(callers?.rows ?? [], app.appId) : []), [app, callers]);
  const notes = callers ? callerNotes(callers) : [];

  return (
    <div className="space-y-6">
      <div>
        <BackLink label="All apps" onClick={onBack} />
        <h2 className="text-lg font-semibold">{app?.title ?? (loading ? 'Loading app' : 'App not found')}</h2>
      </div>
      {error ? <Problem message={error} /> : null}
      {!app && !loading && !error ? <p className="text-sm text-muted-foreground">This app is not in the organization or is deleted.</p> : null}
      {app ? (
        <>
          <AdminSection title="Roles" description="Who holds a seat on this app. You change seats in the app's own Manage page.">
            <ul className="mb-3 space-y-1.5 text-sm">
              {app.members.map((m) => (
                <li key={m.userId} className="flex justify-between gap-3">
                  <span>{m.label}</span>
                  <span className="text-muted-foreground">{m.role}</span>
                </li>
              ))}
              {app.members.length === 0 ? <li className="text-muted-foreground">No seats.</li> : null}
            </ul>
            {orgId ? (
              <Link to={`${orgBasePath(orgId)}/projects/${encodeURIComponent(app.projectId)}`} className="text-sm font-medium text-foreground hover:underline">
                Open the app page
              </Link>
            ) : null}
          </AdminSection>
          <AdminSection title="May call" description="The integrations this app may call, and who allowed it." bodyClassName="p-0">
            {callerError ? (
              <div className="p-4">
                <Problem message={callerError} />
              </div>
            ) : null}
            {notes.length > 0 ? (
              <div className="p-4">
                <Problem message={notes.join(' ')} />
              </div>
            ) : null}
            {callers && rows.length === 0 && notes.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No integration. The app asks through a request.</p> : null}
            {rows.length > 0 ? (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    {['Integration', 'Environment', 'Given by', 'Until', 'Reason'].map((label) => (
                      <th key={label} scope="col" className="px-4 py-2 font-medium">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.grant.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5">{c.integration}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{c.app === '*' ? 'Every app' : c.env === 'all' ? 'All' : c.env}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{givenBy(c.grant)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{untilText(c.grant.ends_at)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{c.grant.reason || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </AdminSection>
          <AdminSection title="Open requests" description="This app asks for more. You decide here.">
            {problem ? <Problem message={problem} /> : null}
            {requests?.length === 0 ? <p className="text-sm text-muted-foreground">No open request for this app.</p> : null}
            <ul className="space-y-3">
              {requests?.map((r) => {
                const row: OpenRequest = { kind: 'app', id: r.id, who: r.requester_label || 'Unknown', what: `asks for ${r.object}`, reason: r.reason, createdAt: r.created_at };
                return (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 text-sm">
                    <span>
                      {row.who} {row.what}{' '}
                      <span className="text-xs text-muted-foreground">
                        on {formatDateTime(r.created_at)}
                        {r.reason ? `, ${r.reason}` : ''}
                      </span>
                    </span>
                    <DecideButtons row={row} busy={busy === r.id} onDecide={decide} />
                  </li>
                );
              })}
            </ul>
          </AdminSection>
        </>
      ) : null}
    </div>
  );
}
